import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, eur } from "@/lib/api";
import { CheckCircle2, Clock, Bike, ChefHat, Circle } from "lucide-react";
import { OrderStatusSkeleton } from "@/components/skeletons/Skeletons";
import haptic from "@/lib/haptic";

const STEPS = [
  { key: "received", label: "Ordine ricevuto", Icon: CheckCircle2 },
  { key: "preparing", label: "In preparazione", Icon: ChefHat },
  { key: "delivering", label: "In consegna", Icon: Bike },
  { key: "delivered", label: "Consegnato", Icon: CheckCircle2 },
];

export default function OrderStatus() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    let stop = false;
    let lastStatus = null;
    const load = async () => {
      try {
        const { data } = await api.get(`/public/orders/${orderId}`);
        if (stop) return;
        // Sprint 0.4 — vibrazione discreta al passaggio "delivered"
        if (lastStatus && lastStatus !== "delivered" && data.customer_status === "delivered") {
          haptic.confirm();
        }
        lastStatus = data.customer_status;
        setOrder(data);
      } catch { /* ignore, retry silenzioso già gestito da api.js Sprint 0.2 */ }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [orderId]);

  if (!order) return <OrderStatusSkeleton />;

  const activeIdx = STEPS.findIndex((s) => s.key === order.customer_status);
  const cancelled = order.customer_status === "cancelled";

  return (
    <div className="min-h-screen px-5 pt-8 pb-10">
      <div className="eyebrow">Ordine {order.code}</div>
      <h1 className="mt-2 text-4xl">Grazie, {order.station_name}</h1>
      <p className="mt-1 text-ink-500">Il tuo ordine è stato inviato al bar. Ti aggiorneremo qui.</p>

      {order.estimated_minutes && !cancelled && order.customer_status !== "delivered" && (
        <div className="mt-6 card-elevated p-5 flex items-center gap-3">
          <Clock className="w-6 h-6 text-bronze-600" strokeWidth={1.5} />
          <div>
            <div className="eyebrow">Tempo indicativo</div>
            <div className="font-serif text-2xl mt-1">circa {order.estimated_minutes} minuti</div>
          </div>
        </div>
      )}

      <section className="mt-6 card-elevated p-5">
        <div className="eyebrow mb-4">Stato ordine</div>
        {cancelled ? (
          <div className="text-red-700">Ordine annullato / rifiutato dal servizio.</div>
        ) : (
          <ol className="space-y-4">
            {STEPS.map((s, idx) => {
              const done = idx <= activeIdx;
              const Icon = done ? s.Icon : Circle;
              return (
                <li key={s.key} data-testid={`status-${s.key}`} className="flex items-center gap-3">
                  <Icon className={`w-6 h-6 ${done ? "text-bronze-600" : "text-ink-500/70"}`} strokeWidth={1.5} />
                  <span className={done ? "font-medium" : "text-ink-700"}>{s.label}</span>
                  {idx === activeIdx && !cancelled && <span className="ml-auto text-xs eyebrow text-bronze-600">In corso</span>}
                </li>
              );
            })}
          </ol>
        )}
      </section>

      <section className="mt-6 card-elevated p-5">
        <div className="eyebrow mb-3">Riepilogo</div>
        <div className="space-y-2 text-sm">
          {order.items.map((i, idx) => (
            <div key={idx} className="flex justify-between">
              <span>{i.quantity}× {i.name}</span>
            </div>
          ))}
          <div className="flex justify-between font-serif text-lg pt-2 border-t border-sand-300">
            <span>Totale</span><span>{eur(order.total_cents)}</span>
          </div>
        </div>
      </section>

      <Link to="/" className="btn-outline mt-6 inline-flex">Torna alla home</Link>
    </div>
  );
}
